import { Question } from "../types/quiz";

export const INITIAL_TIME_PER_QUESTION = 45; // seconds - increased for multiple choice questions
export const QUESTIONS_PER_QUIZ = 20;

export const healthAssessmentQuestions: Question[] = [
  // Personal Information Category
  {
    id: 1,
    category: "Personal Information",
    question: "What is your age group?",
    options: ["Under 18", "18-30", "31-50", "51-65", "Over 65"],
    type: "single",
    explanation: "Age helps us provide age-appropriate health recommendations.",
  },
  {
    id: 2,
    category: "Personal Information",
    question: "What is your biological gender?",
    options: ["Male", "Female", "Prefer not to answer"],
    type: "single",
  },
  {
    id: 3,
    category: "Personal Information",
    question: "What is your current weight status?",
    options: ["Underweight", "Normal weight", "Overweight", "Obese", "Unsure"],
    type: "single",
  },

  // Sleep and Rest Category
  {
    id: 4,
    category: "Sleep and Rest",
    question: "How many hours do you typically sleep per night?",
    options: ["Less than 5 hours", "5-6 hours", "7-8 hours", "9+ hours"],
    type: "single",
    explanation:
      "Quality sleep is essential for physical and mental health recovery.",
  },
  {
    id: 5,
    category: "Sleep and Rest",
    question: "How would you rate your sleep quality?",
    options: ["Very poor", "Poor", "Fair", "Good", "Excellent"],
    type: "single",
  },

  // Physical Activity Category
  {
    id: 6,
    category: "Physical Activity",
    question:
      "How often do you engage in moderate-intensity exercise (30+ minutes)?",
    options: [
      "Never",
      "1-2 times per week",
      "3-4 times per week",
      "5+ times per week",
      "Daily",
    ],
    type: "single",
  },
  {
    id: 7,
    category: "Physical Activity",
    question:
      "What types of physical activities do you regularly participate in? (Select all that apply)",
    options: [
      "Walking/Jogging",
      "Strength Training",
      "Swimming",
      "Cycling",
      "Team Sports",
      "Yoga/Pilates",
      "None",
    ],
    type: "multiple",
    explanation:
      "Variety in exercise helps work different muscle groups and prevents boredom.",
  },

  // Nutrition and Diet Category
  {
    id: 8,
    category: "Nutrition and Diet",
    question: "How many servings of fruits and vegetables do you eat daily?",
    options: ["0-1 servings", "2-3 servings", "4-5 servings", "6+ servings"],
    type: "single",
    explanation:
      "Fruits and vegetables provide essential vitamins, minerals, and fiber for optimal health.",
  },
  {
    id: 9,
    category: "Nutrition and Diet",
    question: "What are your primary protein sources? (Select all that apply)",
    options: [
      "Meat/Poultry",
      "Fish/Seafood",
      "Eggs",
      "Dairy products",
      "Legumes/Beans",
      "Nuts/Seeds",
      "Plant-based alternatives",
    ],
    type: "multiple",
  },
  {
    id: 10,
    category: "Nutrition and Diet",
    question: "How often do you eat processed or fast food?",
    options: [
      "Daily",
      "Several times per week",
      "Once per week",
      "Rarely",
      "Never",
    ],
    type: "single",
  },
  {
    id: 11,
    category: "Nutrition and Diet",
    question: "How much water do you drink daily?",
    options: [
      "Less than 4 glasses",
      "4-6 glasses",
      "7-8 glasses",
      "9+ glasses",
    ],
    type: "single",
  },

  // Mental Health and Stress Category
  {
    id: 12,
    category: "Mental Health and Stress",
    question: "How would you rate your current stress level?",
    options: ["Very low", "Low", "Moderate", "High", "Very high"],
    type: "single",
  },
  {
    id: 13,
    category: "Mental Health and Stress",
    question:
      "What stress management techniques do you use? (Select all that apply)",
    options: [
      "Exercise",
      "Meditation/Mindfulness",
      "Deep breathing",
      "Social support",
      "Hobbies",
      "Professional help",
      "None",
    ],
    type: "multiple",
  },
  {
    id: 14,
    category: "Mental Health and Stress",
    question: "How often do you feel overwhelmed or anxious?",
    options: ["Never", "Rarely", "Sometimes", "Often", "Always"],
    type: "single",
  },

  // Health Habits Category
  {
    id: 15,
    category: "Health Habits",
    question: "Do you use tobacco products?",
    options: ["Never", "Former user (quit)", "Occasionally", "Regularly"],
    type: "single",
  },
  {
    id: 16,
    category: "Health Habits",
    question: "How often do you consume alcohol?",
    options: [
      "Never",
      "Occasionally (1-2 drinks/week)",
      "Moderately (3-7 drinks/week)",
      "Frequently (8+ drinks/week)",
    ],
    type: "single",
  },
  {
    id: 17,
    category: "Health Habits",
    question: "How often do you get regular health check-ups?",
    options: ["Annually", "Every 2 years", "Only when sick", "Rarely/Never"],
    type: "single",
  },

  // Medical History Category
  {
    id: 18,
    category: "Medical History",
    question:
      "Do you have any chronic health conditions? (Select all that apply)",
    options: [
      "Diabetes",
      "High blood pressure",
      "Heart disease",
      "Arthritis",
      "Asthma",
      "PCOD / Insulin Resistance",
      "Depression/Anxiety",
      "None",
    ],
    type: "multiple",
  },
  {
    id: 19,
    category: "Medical History",
    question: "Are you currently taking any medications regularly?",
    options: [
      "No medications",
      "1-2 medications",
      "3-5 medications",
      "6+ medications",
      "Prefer not to answer",
    ],
    type: "single",
  },
  {
    id: 20,
    category: "Medical History",
    question: "Do you have any allergies? (Select all that apply)",
    options: [
      "Food allergies",
      "Environmental allergies",
      "Medication allergies",
      "No known allergies",
      "Prefer not to answer",
    ],
    type: "multiple",
  },
];

// Helper function to get questions by category
export const getQuestionsByCategory = (category: string): Question[] => {
  return healthAssessmentQuestions.filter((q) => q.category === category);
};

// Get all unique categories
export const getCategories = (): string[] => {
  return Array.from(new Set(healthAssessmentQuestions.map((q) => q.category)));
};
